/* Allocate room for the longest answer once, then share it between four boxes;
   side by side, the image takes the same height. */
if (!customElements.get('objectiv-philosophy-accordion')) {
  customElements.define('objectiv-philosophy-accordion', class extends HTMLElement {
    connectedCallback() {
      if (this.lifetime) return;
      this.lifetime = new AbortController();
      this.items = [...this.querySelectorAll('details')];
      this.content = this.closest('.obj-about-philosophy__content');
      this.layout = this.closest('.obj-about-philosophy');
      this.media = this.layout.querySelector('.obj-about-philosophy__media');
      const options = { signal: this.lifetime.signal };
      this.addEventListener('toggle', event => {
        if (!this.items.includes(event.target)) return;
        if (event.target.open) this.items.forEach(item => { if (item !== event.target) item.open = false; });
        this.render();
      }, { ...options, capture: true });
      this.observer = new ResizeObserver(() => {
        const size = `${this.clientWidth}:${this.media.clientWidth}`;
        if (size !== this.lastSize) { this.lastSize = size; this.measure(); }
      });
      this.observer.observe(this);
      this.observer.observe(this.media);
      document.fonts.addEventListener('loadingdone', () => this.measure(), options);
      this.measure();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.lifetime?.abort();
      this.lifetime = null;
    }

    measure() {
      // Measure a plain, hidden copy; it has no group names or custom-element lifecycle.
      const probe = document.createElement('div');
      probe.className = 'obj-about-philosophy__accordion is-measuring';
      probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;width:${this.getBoundingClientRect().width}px`;
      for (const item of this.items) {
        const clone = item.cloneNode(true);
        clone.removeAttribute('name'); clone.open = true; probe.append(clone);
      }
      this.content.append(probe);
      this.closed = [...probe.children].map(item => item.querySelector('summary').getBoundingClientRect().height + 2);
      this.answers = [...probe.children].map(item => item.querySelector('.obj-about-philosophy__copy').getBoundingClientRect().height);
      const longest = Math.max(...this.answers);
      probe.remove();
      const heading = this.content.querySelector('h2');
      const style = getComputedStyle(this.content);
      const above = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + heading.getBoundingClientRect().height + parseFloat(getComputedStyle(heading).marginBottom);
      const needed = Math.ceil(this.closed.reduce((sum, height) => sum + height, 0) + longest + 42);
      const columns = getComputedStyle(this.layout).gridTemplateColumns.split(' ').length;
      if (columns > 1) {
        // Side by side, the image and accordion share one height: the image's
        // natural height, but never short of the longest answer and never more
        // than 64px of spare room. The image crops to fit.
        const natural = this.media.getBoundingClientRect().width * 2400 / 1792 - above;
        this.height = Math.max(needed, Math.min(natural, needed + 64));
        this.media.style.setProperty('--philosophy-media-height', `${this.height + above}px`);
      } else {
        this.height = Math.max(this.media.getBoundingClientRect().height - above, needed);
        this.media.style.removeProperty('--philosophy-media-height');
      }
      this.classList.remove('is-bounded');
      this.style.setProperty('--philosophy-height', `${this.height}px`);
      this.render();
      this.classList.add('is-ready');
    }

    render() {
      if (!this.closed) return;
      const active = this.items.findIndex(item => item.open);
      // The open box grows only to fit its own answer plus a little room; the
      // other boxes share what is left, shrinking together by that growth.
      const space = this.height - 42, rows = this.closed.map(() => space / 4);
      if (active >= 0) {
        const others = this.closed.reduce((sum, height, index) => sum + (index === active ? 0 : height), 0);
        rows[active] = Math.min(space - others, this.closed[active] + this.answers[active] + 12);
        const share = (space - rows[active]) / 3;
        rows.forEach((height, index) => { if (index !== active) rows[index] = Math.max(this.closed[index], share); });
      }
      this.items.forEach((item, index) => item.style.setProperty('--philosophy-answer-height', `${Math.max(0, rows[index] - this.closed[index])}px`));
      this.style.setProperty('--philosophy-rows', rows.map(height => `${height}px`).join(' '));
    }
  });
}
